import React, { Suspense } from "react";

/*
  마크다운 에디터는 remark/rehype 계열 의존성을 통째로 끌고 온다.
  포트폴리오 추가/수정 모달을 열었을 때만 필요하므로 지연 로딩해서
  첫 화면 번들에 싣지 않는다.

  /nohighlight 엔트리는 코드 블록 문법 강조(rehype-prism-plus + refractor)를
  뺀 빌드다. 포트폴리오 본문에 소스 코드를 넣을 일이 없는데 그 하나 때문에
  청크가 두 배 이상 커진다.
*/
const MDEditor = React.lazy(() => import("@uiw/react-md-editor/nohighlight"));

interface PortfolioDescriptionEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const EditorPlaceholder = () => (
  <div
    className="w-full min-h-[320px] / rounded-[8px] border border-[#e0e0e0]
      / bg-gray-50 / flex items-center justify-center / text-[13px] text-[#888]"
  >
    편집기를 불러오는 중입니다...
  </div>
);

/**
 * 포트폴리오 상세 설명 입력기.
 *
 * 예전에는 순수 `<textarea>` 였고 쇼핑몰이 `<%= %>` 로 그대로 출력해서
 * 줄바꿈조차 반영되지 않았다. 이제 마크다운으로 쓰고 쇼핑몰이 렌더링한다.
 *
 * **하위호환 주의**: 마크다운 도입 이전에 저장된 본문은 plain text 다.
 * 거기에 `*`, `_`, `#` 같은 문자가 섞여 있으면 의도치 않게 서식으로 해석된다.
 * 저장된 값을 일괄 변환(이스케이프)하지는 않는다 — 멀쩡한 본문까지 백슬래시로
 * 더럽히고 되돌릴 수도 없기 때문이다. 대신 오른쪽 미리보기로 관리자가 실제
 * 출력 결과를 보고 필요한 곳만 직접 고치게 한다.
 */
export const PortfolioDescriptionEditor = ({
  value,
  onChange,
}: PortfolioDescriptionEditorProps) => (
  /*
    에디터는 data-color-mode 로 테마를 정한다. 지정하지 않으면 방문자
    OS 의 다크 모드를 따라가서 흰 배경인 관리자 화면 안에서 혼자 검게 나온다.
  */
  <div data-color-mode="light">
    <Suspense fallback={<EditorPlaceholder />}>
      <MDEditor
        value={value}
        onChange={(next) => onChange(next ?? "")}
        height={320}
        preview="live"
        textareaProps={{
          placeholder: "작업 내용을 입력하세요. 마크다운 문법을 쓸 수 있습니다.",
        }}
      />
    </Suspense>
  </div>
);
